import React, { useContext } from 'react';
import { Tooltip } from '@mantine/core';
import MyUserContext from './../myUserContext.js';
import { anonymousLang } from './../localize.js';

const PlatformRatings = ({ art, fullMode = false, center = true, twoColumns = false, showVotes = false, hidePairVotes = false }) => {
  const votesVisible = fullMode || showVotes;
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : anonymousLang();
  const rawInfos = art.otherPlatformInfos || [];

  // Fold the separate "<x>User" entry into the paired critic entry so
  // both scores render under one icon (e.g. Metacritic "86/37 6.0/1028",
  // Rotten Tomatoes "60/47 92/1210"). If only one of the two exists, we
  // keep whichever it is with no visual change.
  const mergePair = (list, criticName, userName) => {
    const userInfo = list.find(i => i.name === userName);
    if (!userInfo) return list;
    const criticInfo = list.find(i => i.name === criticName);
    const merged = {
      name: criticName,
      link: (criticInfo && criticInfo.link) || userInfo.link,
      score: criticInfo ? criticInfo.score : undefined,
      votes: criticInfo ? criticInfo.votes : undefined,
      userScore: userInfo.score,
      userVotes: userInfo.votes,
    };
    return list
      .filter(i => i.name !== criticName && i.name !== userName)
      .concat(criticInfo || userInfo ? [merged] : []);
  };
  const otherPlatformInfos = mergePair(
    mergePair(
      mergePair(rawInfos, 'metacritic', 'metacriticUser'),
      'rottenTomatoes',
      'rottenTomatoesAudience',
    ),
    'openCritic',
    'openCriticUser',
  );

  const formatRating = (rating) => (rating === 0 ? '-' : rating.toFixed(1));
  const formatMetascore = (rating) => (rating === 0 ? '-' : Math.round(rating).toString());
  const iconSizeClass = 
    fullMode
      ? "max-w-24 max-h-10"
      : "max-w-4 max-h-4 opacity-95";
  const textStyle = { color: 'var(--mantine-color-dimmed)' };
  const textClass =
    fullMode
      ? "text-sm font-medium leading-none"
      : "text-[11px] font-medium leading-none";

  const getIconForName = (name) => {
    switch (name.toLowerCase()) {
      case 'imdb': return '/imdb_small_icon.png';
      case 'kinopoisk': return '/kinopoisk_small_icon.png';
      case 'applebooks': return '/applebooks_icon.png';
      case 'steam': return '/steam_icon.png';
      case 'rottentomatoes': return '/tomato_icon.png';
      case 'reddit': return '/reddit_icon.png';
      case 'amazon': return '/amazon_icon.png';
      case 'goodreads': return '/goodreads_icon.png';
      case 'metacritic': return '/metacritic_icon.png';
      case 'opencritic': return '/opencritic_icon.png';
      case 'wikipedia': return '/wiki_icon.png';
      case 'litres': return '/litres_icon.png';
      case 'livelib': return '/livelib_icon.png';
      default: return null;
    }
  };

  const renderIconOrText = (icon, fullMode) => {
    const title = icon === '/metacritic_icon.png' ? 'Рейтинг платформы Metacritics' : (icon === '/opencritic_icon.png' ? 'Рейтинг платформы OpenCritic' : '');
    const additionalClasses = icon === '/wiki_icon.png' ? 'rounded-md p-1' : '';
    const additionalStyle = icon === '/wiki_icon.png' ? { backgroundColor: 'var(--mantine-color-body)' } : {};
    return <img src={icon} title={title} className={`rounded-md ${iconSizeClass} ${additionalClasses}`} style={additionalStyle} />;
  };

  const renderItem = ({ name, link, score, votes, userScore, userVotes }) => {
    const icon = getIconForName(name);
    if (!icon) return null;
    // Wikipedia carries no rating — show it as a clickable icon on the
    // art page only (fullMode), and skip it entirely elsewhere so hover
    // overlays / detail panels / thumbnails stay rating-only.
    if (name === 'wikipedia') {
      if (!fullMode || !link) return null;
      return (
        <div key={name} className="flex items-center">
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        </div>
      );
    }
    // Apple Books / iTunes: never surface the icon. The rating is
    // unreliable so we can't show the number, and we don't want the
    // logo either.
    if (name === 'appleBooks') {
      return null;
    }
    // Litres: show the icon as a clickable link out, but never the
    // rating — the number they surface is meaningless.
    if (name === 'litres') {
      if (!fullMode || !link) return null;
      return (
        <div key={name} className="flex items-center">
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        </div>
      );
    }
    const hasCritic = score != null && score !== 0;
    const hasUser = userScore != null && userScore !== 0;
    // Keep platform icons in detail panels and on the art page before
    // scores land. The art page omits missing-score placeholders.
    if (!fullMode && !showVotes && !hasCritic && !hasUser) return null;

    // Merged critic + audience/user view:
    //   Metacritic     "86/37 6.0/1028"     (Metascore int + user score 1dp)
    //   Rotten Tomatoes "60/47 92/1,210"    (both sides are integer %)
    //   OpenCritic     "82/49 90"           (Top Critic Average + Player Rating, both 0..100)
    // Each half is a tap/hover target labelling itself so users know
    // which score is which.
    const isPairPlatform = name === 'metacritic' || name === 'rottenTomatoes' || name === 'openCritic';
    // In showVotes / fullMode always render the two-half view for the
    // pair platforms; fullMode hides whichever side lacks a score.
    const isMerged = isPairPlatform && (votesVisible || hasUser || userScore !== undefined);
    if (isMerged) {
      const isRT = name === 'rottenTomatoes';
      // RT is displayed as a percentage on rottentomatoes.com — append '%'
      // to the score half so users recognize it. Metascore stays bare.
      const isOC = name === 'openCritic';
      const critFmt = isRT ? (v) => `${formatMetascore(v)}%` : formatMetascore;
      const userFmt = isRT ? (v) => `${formatMetascore(v)}%` : (isOC ? formatMetascore : formatRating);
      const sep = ' / ';
      // Placeholder logic:
      //   compact (no votesVisible): only paper over votes when a score is
      //     present ("60/47" or "-/47"); if both missing, skip the half.
      //   votesVisible: show "score/votes", padding missing values with '-'.
      //   fullMode: omit a half entirely when its score is missing.
      const critScoreStr = hasCritic ? critFmt(score) : '-';
      const critVotesStr = votes ? votes.toLocaleString() : '-';
      const userScoreStr = hasUser ? userFmt(userScore) : '-';
      const userVotesStr = userVotes ? userVotes.toLocaleString() : '-';
      const criticText = fullMode && !hasCritic ? '' : hidePairVotes
        ? (hasCritic ? critScoreStr : '')
        : votesVisible
          ? `${critScoreStr}${sep}${critVotesStr}`
          : (hasCritic || votes
              ? `${critScoreStr}${votes ? `${sep}${critVotesStr}` : ''}`
              : '');
      const userText = fullMode && !hasUser ? '' : hidePairVotes
        ? (hasUser ? userScoreStr : '')
        : votesVisible
          ? `${userScoreStr}${sep}${userVotesStr}`
          : (hasUser || userVotes
              ? `${userScoreStr}${userVotes ? `${sep}${userVotesStr}` : ''}`
              : '');
      const criticLabel = language === 'ru' ? 'Рейтинг критиков' : "Critics' rating";
      const userLabel = language === 'ru'
        ? (name === 'rottenTomatoes' ? 'Рейтинг зрителей' : 'Рейтинг пользователей')
        : (name === 'rottenTomatoes' ? "Audience rating" : "Users' rating");
      // RT no longer needs the dotted underline — the '%' + tooltip
      // already read as interactive on desktop and mobile.
      const partClass = isRT
        ? `${textClass} cursor-pointer`
        : `${textClass} cursor-pointer underline decoration-dotted underline-offset-2`;
      // Emulate the RT fresh/rotten signal without their logos:
      //   <60% rotten → green, ≥60% fresh → red.
      const rtStyleFor = (pct) => {
        if (pct == null || pct === 0) return {};
        if (pct < 60) return { color: 'rgb(101, 165, 76)' };
        return {};
      };
      const critStyle = isRT && hasCritic ? { ...textStyle, ...rtStyleFor(score) } : textStyle;
      const userStyleRT = isRT && hasUser ? { ...textStyle, ...rtStyleFor(userScore) } : textStyle;
      return (
        <div key={name} className="flex items-center space-x-1">
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {renderIconOrText(icon, fullMode)}
            </a>
          ) : (
            renderIconOrText(icon, fullMode)
          )}
          {criticText && (
            <Tooltip label={criticLabel} withArrow events={{ hover: true, focus: true, touch: true }}>
              <span className={partClass} style={critStyle} tabIndex={0}>{criticText}</span>
            </Tooltip>
          )}
          {userText && (
            <Tooltip label={userLabel} withArrow events={{ hover: true, focus: true, touch: true }}>
              <span className={partClass} style={userStyleRT} tabIndex={0}>{userText}</span>
            </Tooltip>
          )}
        </div>
      );
    }

    return (
      <div key={name} className="flex items-center space-x-0.5">
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        ) : (
          renderIconOrText(icon, fullMode)
        )}
        {(() => {
          // The art page keeps the linked icon without a missing-score placeholder.
          if (fullMode && !hasCritic) return null;
          const hasScore = score != null && (fullMode || score !== 0);
          const hasVoteInline = votesVisible && votes !== undefined && votes !== 0;
          // In compact non-showVotes mode we drop the whole span when
          // neither score nor votes is present. In showVotes/fullMode we
          // always render "score / votes", padding with '-' where needed
          // so linkage stays visible even before scrapes land.
          if (!votesVisible && !hasScore && !hasVoteInline) return null;
          const scoreText = hasScore
            ? (name === 'metacritic' || name === 'openCritic' ? formatMetascore(score) : formatRating(score))
            : '-';
          const showVotesPart = votesVisible || hasVoteInline;
          const votesText = hasVoteInline
            ? votes.toLocaleString()
            : (votesVisible ? '-' : '');
          return (
            <span className={textClass} style={textStyle}>
              {scoreText}{showVotesPart ? ` / ${votesText}` : ''}
            </span>
          );
        })()}
      </div>
    );
  };

  if (twoColumns) {
    const visibleItems = otherPlatformInfos.filter(({ name, score, userScore }) => {
      if (!getIconForName(name)) return false;
      const hasAnyScore = (score !== undefined && score !== 0) || (userScore !== undefined && userScore !== 0);
      if (!fullMode && !hasAnyScore) return false;
      return true;
    });
    // A pair platform (metacritic / rottenTomatoes) that actually
    // renders both halves is visually wide — pairing it with another
    // platform on the same row squeezes both. Give wide items their
    // own row and only pair up the single-value entries.
    const isWide = ({ name, score, userScore }) => {
      const isPairPlatform = name === 'metacritic' || name === 'rottenTomatoes' || name === 'openCritic';
      if (!isPairPlatform) return false;
      const hasCritic = score != null && score !== 0;
      const hasUser = userScore != null && userScore !== 0;
      // In showVotes / fullMode both halves are always drawn (padded
      // with '-'); elsewhere only when data exists on both sides.
      return votesVisible || (hasCritic && hasUser);
    };
    const rows = [];
    let i = 0;
    while (i < visibleItems.length) {
      const cur = visibleItems[i];
      const next = visibleItems[i + 1];
      if (isWide(cur)) {
        rows.push([cur]);
        i += 1;
      } else if (next && isWide(next)) {
        rows.push([cur]);
        i += 1;
      } else if (next) {
        rows.push([cur, next]);
        i += 2;
      } else {
        rows.push([cur]);
        i += 1;
      }
    }
    return (
      <div className="flex flex-col gap-0.5 text-xs font-medium leading-none opacity-95 w-fit">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-1">
            {row.map(renderItem)}
          </div>
        ))}
      </div>
    );
  }

  const items = otherPlatformInfos.map(renderItem).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center ${fullMode ? 'gap-x-4 gap-y-2' : 'gap-1'} text-xs font-medium leading-none opacity-95${center ? ' justify-center' : ''}`}>
      {items}
    </div>
  );
};

export default PlatformRatings;
